import { useEffect, useRef, useState } from 'react'

/** Tvar /version.json — stejný kontrakt jako v portal-fe. */
interface VersionInfo {
  commit_sha: string
  commit_message: string
}

const POLL_MS = 60_000

/**
 * Hlídá, jestli se v mezičase nenasadila nová verze frontendu.
 *
 * Soubor /version.json vzniká v CI z právě sestaveného commitu (viz Dockerfile
 * a .github/workflows/fe.yml). První stažená hodnota je baseline — tedy verze,
 * na které tenhle tab běží. Když se při dalším dotazu commit liší, znamená to,
 * že mezitím proběhl deploy, a nabídneme načtení znovu.
 *
 * Reload jenom nabízíme, nikdy nevynucujeme: uživatel může mít rozdělaný
 * formulář. Běžící timer je na serveru, o ten reloadem nepřijde.
 */
export default function UpdateBanner() {
  const baseline = useRef<string | null>(null)
  const [update, setUpdate] = useState<VersionInfo | null>(null)

  useEffect(() => {
    // V devu běží vite server, který /version.json nemá — nemá co hlídat.
    if (window.location.hostname === 'localhost') return

    const controller = new AbortController()
    let timer: ReturnType<typeof setInterval> | undefined

    const check = async () => {
      try {
        const response = await fetch('/version.json', { cache: 'no-store', signal: controller.signal })
        if (!response.ok) return
        const data: VersionInfo = await response.json()
        if (!data?.commit_sha) return

        if (baseline.current === null) {
          baseline.current = data.commit_sha
        } else if (data.commit_sha !== baseline.current) {
          setUpdate(data)
          clearInterval(timer) // banner už visí, dál se ptát nemá smysl
        }
      } catch {
        // Offline nebo zrovna probíhající deploy — zkusíme to za minutu znovu.
      }
    }

    check()
    timer = setInterval(check, POLL_MS)

    return () => {
      controller.abort()
      clearInterval(timer)
    }
  }, [])

  if (!update) return null

  return (
    <div className="border-b border-amber-300 bg-amber-100">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-2.5">
        <div className="text-sm text-amber-900">
          <span className="font-semibold">Je k dispozici nová verze aplikace.</span>
          {update.commit_message && (
            <span className="ml-2 text-amber-800/80">{update.commit_message}</span>
          )}
        </div>
        <button
          onClick={() => location.reload()}
          className="ml-auto rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-amber-700"
        >
          Načíst znovu
        </button>
      </div>
    </div>
  )
}
